import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiMinus } = FiIcons;

const faqData = [
  {
    question: "What is your typical project timeline?",
    answer: "Most of our AI integration and custom software projects range from 8 to 16 weeks for the initial production-ready version. We work in 2-week sprints, so you see tangible progress every fortnight."
  },
  {
    question: "Do I own the intellectual property (IP)?",
    answer: "Yes. Upon completion and final payment, you own 100% of the custom code, architectural designs, and integrated models we build for your specific business. We believe in building assets for our clients, not renting them."
  },
  {
    question: "How do you handle data privacy and security?",
    answer: "Security is built-in, not bolted on. We follow industry best practices for data encryption, secure API design, and cloud-native security protocols. For AI projects, we focus on 'private-by-design' implementations that keep your data within your own infrastructure."
  },
  {
    question: "What industries do you specialize in?",
    answer: "While we are technology-focused (.NET and AI), our deepest experience lies in manufacturing infrastructure, high-throughput financial systems, and logistics automation across the UK and Indian markets."
  },
  {
    question: "Can you work with our existing internal IT team?",
    answer: "Absolutely. We often act as a 'force multiplier' for internal teams—taking on the complex R&D or architectural heavy lifting while ensuring your internal team is fully briefed and capable of long-term maintenance."
  }
];

const FAQItem = ({ question, answer, isOpen, toggle }) => {
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={toggle}
        className="w-full py-6 flex items-center justify-between text-left group transition-all"
      >
        <span className={`text-lg font-bold transition-colors ${isOpen ? 'text-cyan-600' : 'text-slate-900 group-hover:text-cyan-500'}`}>
          {question}
        </span>
        <div className={`flex-shrink-0 ml-4 w-8 h-8 rounded-full border flex items-center justify-center transition-all ${isOpen ? 'bg-cyan-500 border-cyan-500 text-white rotate-180' : 'border-slate-200 text-slate-400 group-hover:border-cyan-500 group-hover:text-cyan-500'}`}>
          <SafeIcon icon={isOpen ? FiMinus : FiPlus} />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-8 text-slate-600 leading-relaxed max-w-3xl">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-24 bg-white relative overflow-hidden" id="faq">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-slate-50/50 -skew-x-12 translate-x-1/2 pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <div className="sticky top-32">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-50 border border-cyan-200 mb-6">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">Help Center</span>
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
                Common <br />
                <span className="text-gradient-cyan">Questions</span>
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Everything you need to know about partnering with Aetrix Logic. Can't find what you're looking for? Reach out to us.
              </p>
              <a 
                href="/contact" 
                className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest text-slate-900 hover:text-cyan-600 transition-colors"
              >
                Go to Contact Page <SafeIcon icon={FiPlus} className="rotate-45" />
              </a>
            </div>
          </div>
          
          <div className="lg:col-span-8 bg-white border border-slate-100 p-8 md:p-12 shadow-sm">
            {faqData.map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index}
                toggle={() => setOpenIndex(openIndex === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
